import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, initFlatpickrInShadow, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { SankeyMoverTypeDistributionConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

type SankeyMetric = 'change' | 'volume' | 'price' | 'relative_volume' | 'market_cap';
type MoverTypeKey = 'Gainer' | 'Loser' | 'MostActive';

interface SankeyDetailedLink {
    source_idx?: number;
    target_idx?: number;
    source_label?: string;
    target_label?: string;
    mover_type?: string;
    symbol?: string;
    flow_value?: number;
    sort_value?: number;
    change?: number;
    volume?: number;
    price?: number;
    relative_volume?: number;
    market_cap?: number;
}

interface SankeyChartLink {
    source: string;
    target: string;
    value: number;
    lineStyle: { color: string; opacity: number };
    detail: SankeyDetailedLink;
}

interface SankeyChartNode {
    name: string;
    depth: number;
    itemStyle?: { color: string };
}

interface SankeyApiData {
    parameters?: {
        sort_metric?: string;
        flow_metric?: string;
        date_filter?: string;
        min_threshold?: number;
        top_n?: number;
        mover_types?: string[];
        show_absolute?: boolean;
    };
    summary_statistics?: Record<string, number>;
    sankey_data?: {
        node_labels?: string[];
        links?: {
            source?: number[];
            target?: number[];
            value?: number[];
        };
    };
    detailed_links?: SankeyDetailedLink[];
}

const MOVER_TYPE_LABELS: Record<MoverTypeKey, string> = {
    Gainer: 'Gainers',
    Loser: 'Loser',
    MostActive: 'Most Active'
};

const MOVER_TYPE_COLORS: Record<string, string> = {
    Gainer: '#6FF19A',
    Gainers: '#6FF19A',
    Loser: '#FF2B2B',
    Losers: '#FF2B2B',
    MostActive: '#FF8C42',
    'Most Active': '#FF8C42'
};

const METRIC_OPTIONS: Array<{ value: SankeyMetric; label: string }> = [
    { value: 'change', label: 'Change' },
    { value: 'volume', label: 'Volume' },
    { value: 'price', label: 'Price' },
    { value: 'relative_volume', label: 'Relative Volume' },
    { value: 'market_cap', label: 'Market Cap' }
];

const TOP_N_OPTIONS = [5, 10, 15, 20];

const MOVER_TYPE_ORDER: MoverTypeKey[] = ['Gainer', 'Loser', 'MostActive'];

export class TraderverseSankeyMoverTypeDistribution {
    private container: HTMLElement;
    private config: SankeyMoverTypeDistributionConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private apiData: SankeyApiData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private legendStates: Record<MoverTypeKey, boolean> = {
        Gainer: true,
        Loser: true,
        MostActive: true
    };
    private filters = {
        sortMetric: 'change' as SankeyMetric,
        flowMetric: 'change' as SankeyMetric,
        dateFilter: new Date().toISOString().slice(0, 10),
        minThreshold: 0 as number | '',
        topN: 5 as number | '',
        moverTypes: ['Gainer', 'Loser', 'MostActive'] as MoverTypeKey[],
        showAbsolute: false
    };

    constructor(config: SankeyMoverTypeDistributionConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<SankeyMoverTypeDistributionConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            sortMetric: 'change',
            flowMetric: 'change',
            dateFilter: new Date().toISOString().slice(0, 10),
            minThreshold: 0,
            topN: 5,
            moverTypes: ['Gainer', 'Loser', 'MostActive'],
            showAbsolute: false
        };
    }

    private applyConfigFilters(): void {
        if (this.config.sortMetric) this.filters.sortMetric = this.config.sortMetric as SankeyMetric;
        if (this.config.flowMetric) this.filters.flowMetric = this.config.flowMetric as SankeyMetric;
        if (this.config.dateFilter) this.filters.dateFilter = this.config.dateFilter;
        if (typeof this.config.minThreshold === 'number') this.filters.minThreshold = this.config.minThreshold;
        if (typeof this.config.topN === 'number') this.filters.topN = this.config.topN;
        if (Array.isArray(this.config.moverTypes) && this.config.moverTypes.length) {
            this.filters.moverTypes = this.config.moverTypes as MoverTypeKey[];
        }
        if (typeof this.config.showAbsolute === 'boolean') this.filters.showAbsolute = this.config.showAbsolute;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const metricOptions = (selected: string) =>
            METRIC_OPTIONS.map(
                ({ value, label }) =>
                    `<option value="${value}"${selected === value ? ' selected' : ''}>${label}</option>`
            ).join('');

        const topNOptions = TOP_N_OPTIONS.map(
            (n) => `<option value="${n}"${this.filters.topN === n ? ' selected' : ''}>${n}</option>`
        ).join('');

        const moverTypeCheckboxes = (Object.keys(MOVER_TYPE_LABELS) as MoverTypeKey[])
            .map((key) => {
                const checked = this.filters.moverTypes.includes(key) ? 'checked' : '';
                return `
                    <label class="mover-type-chip">
                        <input type="checkbox" class="filter-mover-type" value="${key}" ${checked} />
                        <span style="background:${MOVER_TYPE_COLORS[key]}"></span>
                        ${MOVER_TYPE_LABELS[key]}
                    </label>`;
            })
            .join('');

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Sankey Mover Type Distribution</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <i class="pi pi-ellipsis-v"></i>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <div class="item">
                        <div class="legendsCollection">
                            ${(Object.keys(MOVER_TYPE_LABELS) as MoverTypeKey[])
                                .map(
                                    (key) =>
                                        `<span class="toggle-series active" data-mover="${key}" style="background-color:${MOVER_TYPE_COLORS[key]}"></span>`
                                )
                                .join('')}
                        </div>
                    </div>
                    <div class="item dateRange">
                        <input type="text" class="date-input" readonly />
                        <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/></svg></i>
                    </div>
                    <div class="item">
                        <label>Sort</label>
                        <select class="filter-sort-metric dropdown-btn">${metricOptions(this.filters.sortMetric)}</select>
                    </div>
                    <div class="item">
                        <label>Flow</label>
                        <select class="filter-flow-metric dropdown-btn">${metricOptions(this.filters.flowMetric)}</select>
                    </div>
                    <div class="item">
                        <label>Top N</label>
                        <select class="filter-top-n dropdown-btn">${topNOptions}</select>
                    </div>
                    <div class="item">
                        <label>Min Threshold</label>
                        <input type="number" class="filter-min-threshold" step="0.01" placeholder="0" value="${this.filters.minThreshold}" />
                    </div>
                    <div class="item mover-type-filters">
                        ${moverTypeCheckboxes}
                    </div>
                    <div class="item">
                        <div class="checkBoxField">
                            <input class="filter-show-absolute" type="checkbox" ${this.filters.showAbsolute ? 'checked' : ''} />
                            <label>Absolute <i>✓</i></label>
                        </div>
                    </div>
                </div>
                <div class="chartDv" id="sankeyMoverTypeContainer">
                    <div id="chartContainer" class="chart coverDv"></div>
                </div>
            </div>
        </div>`;

        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        setupHeaderPin(this.shadowRoot, this.config);

        const legendEls = this.shadowRoot.querySelectorAll('.legendsCollection .toggle-series');
        legendEls.forEach((el) => {
            el.addEventListener('click', () => {
                const mover = (el as HTMLElement).dataset.mover as MoverTypeKey;
                if (!mover) return;
                this.legendStates[mover] = !this.legendStates[mover];
                el.classList.toggle('active', this.legendStates[mover]);
                this.drawChart();
            });
        });

        const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
        if (dateInput) {
            dateInput.value = this.filters.dateFilter;
            initFlatpickrInShadow(dateInput, this.shadowRoot, {
                mode: 'single',
                dateFormat: 'Y-m-d',
                defaultDate: this.filters.dateFilter,
                onChange: (selectedDates: Date[]) => {
                    if (selectedDates.length === 1) {
                        this.filters.dateFilter = selectedDates[0].toISOString().slice(0, 10);
                        dateInput.value = this.filters.dateFilter;
                        this.setupChart();
                    }
                }
            });
        }

        const sortMetricEl = this.shadowRoot.querySelector('.filter-sort-metric') as HTMLSelectElement;
        const flowMetricEl = this.shadowRoot.querySelector('.filter-flow-metric') as HTMLSelectElement;
        const topNEl = this.shadowRoot.querySelector('.filter-top-n') as HTMLSelectElement;
        const minThresholdEl = this.shadowRoot.querySelector('.filter-min-threshold') as HTMLInputElement;
        const showAbsoluteEl = this.shadowRoot.querySelector('.filter-show-absolute') as HTMLInputElement;
        const moverTypeEls = this.shadowRoot.querySelectorAll('.filter-mover-type') as NodeListOf<HTMLInputElement>;

        const updateFiltersAndFetch = () => {
            this.filters.sortMetric = sortMetricEl.value as SankeyMetric;
            this.filters.flowMetric = flowMetricEl.value as SankeyMetric;
            this.filters.topN = topNEl.value ? Number(topNEl.value) : 5;
            this.filters.minThreshold = this.readNumberInput(minThresholdEl);
            this.filters.showAbsolute = showAbsoluteEl.checked;
            this.filters.moverTypes = Array.from(moverTypeEls)
                .filter((el) => el.checked)
                .map((el) => el.value as MoverTypeKey);
            this.setupChart();
        };

        sortMetricEl?.addEventListener('change', updateFiltersAndFetch);
        flowMetricEl?.addEventListener('change', updateFiltersAndFetch);
        topNEl?.addEventListener('change', updateFiltersAndFetch);
        minThresholdEl?.addEventListener('change', updateFiltersAndFetch);
        showAbsoluteEl?.addEventListener('change', updateFiltersAndFetch);
        moverTypeEls.forEach((el) => el.addEventListener('change', updateFiltersAndFetch));
    }

    private readNumberInput(input: HTMLInputElement | null): number | '' {
        if (!input || input.value.trim() === '') return '';
        const value = parseFloat(input.value);
        return Number.isNaN(value) ? '' : value;
    }

    private buildQueryParams(): Record<string, string | number> {
        const query: Record<string, string | number> = {
            sortMetric: this.filters.sortMetric,
            flowMetric: this.filters.flowMetric,
            showAbsolute: String(this.filters.showAbsolute)
        };
        if (this.filters.dateFilter) query.dateFilter = this.filters.dateFilter;
        if (this.filters.minThreshold !== '') query.minThreshold = this.filters.minThreshold;
        if (this.filters.topN !== '') query.topN = this.filters.topN;
        if (this.filters.moverTypes.length) query.moverTypes = this.filters.moverTypes.join(',');
        return query;
    }

    private getMoverTypeFromLabel(label: string): string | null {
        const normalized = label.trim();
        if (MOVER_TYPE_COLORS[normalized]) return normalized;
        const entry = Object.entries(MOVER_TYPE_LABELS).find(([, v]) => v === normalized);
        return entry ? entry[0] : null;
    }

    private getLinkColor(sourceLabel: string): string {
        const moverType = this.getMoverTypeFromLabel(sourceLabel);
        return (moverType && MOVER_TYPE_COLORS[moverType]) || '#8B6CFF';
    }

    private isLinkVisible(sourceLabel: string): boolean {
        const moverType = this.getMoverTypeFromLabel(sourceLabel) as MoverTypeKey | null;
        if (!moverType) return true;
        return this.legendStates[moverType] !== false;
    }

    private formatMetricLabel(metric: string): string {
        const option = METRIC_OPTIONS.find((m) => m.value === metric);
        return option?.label || metric;
    }

    private formatFlowValue(value: number): string {
        if (!Number.isFinite(value)) return '-';
        if (this.filters.showAbsolute) return Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    private getDeduplicatedDetailedLinks(): SankeyDetailedLink[] {
        const detailedLinks = this.apiData?.detailed_links;
        if (Array.isArray(detailedLinks) && detailedLinks.length) {
            const linkMap = new Map<string, SankeyDetailedLink>();
            detailedLinks.forEach((link) => {
                const source = link.source_label || link.mover_type || '';
                const target = link.target_label || link.symbol || '';
                if (!source || !target) return;
                const key = `${source}::${target}`;
                if (!linkMap.has(key)) linkMap.set(key, link);
            });
            return Array.from(linkMap.values());
        }

        const nodeLabels = this.apiData?.sankey_data?.node_labels;
        const rawLinks = this.apiData?.sankey_data?.links;
        if (!Array.isArray(nodeLabels) || !nodeLabels.length || !rawLinks?.source?.length) return [];

        const linkMap = new Map<string, SankeyDetailedLink>();
        rawLinks.source.forEach((sourceIdx, i) => {
            const targetIdx = rawLinks.target?.[i];
            const sourceLabel = nodeLabels[sourceIdx] || '';
            const targetLabel = nodeLabels[targetIdx ?? -1] || '';
            if (!sourceLabel || !targetLabel) return;

            const key = `${sourceLabel}::${targetLabel}`;
            if (linkMap.has(key)) return;

            linkMap.set(key, {
                source_idx: sourceIdx,
                target_idx: targetIdx,
                source_label: sourceLabel,
                target_label: targetLabel,
                flow_value: Number(rawLinks.value?.[i]) || 0
            });
        });
        return Array.from(linkMap.values());
    }

    private buildChartPayload(): { nodes: SankeyChartNode[]; links: SankeyChartLink[] } {
        const dedupedLinks = this.getDeduplicatedDetailedLinks().filter((link) =>
            this.isLinkVisible(link.source_label || '')
        );

        const moverTypes = new Set<string>();
        const symbolFlowTotals = new Map<string, number>();

        dedupedLinks.forEach((link) => {
            const source = link.source_label || '';
            const target = link.target_label || '';
            const flowValue = Math.abs(Number(link.flow_value) || 0);
            if (source) moverTypes.add(source);
            if (target) {
                symbolFlowTotals.set(target, (symbolFlowTotals.get(target) || 0) + flowValue);
            }
        });

        const orderedMoverTypes: string[] = [
            ...MOVER_TYPE_ORDER.filter((type) => moverTypes.has(type)),
            ...Array.from(moverTypes).filter((type) => !MOVER_TYPE_ORDER.includes(type as MoverTypeKey))
        ];

        const orderedSymbols = Array.from(symbolFlowTotals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([symbol]) => symbol);

        const nodes: SankeyChartNode[] = [
            ...orderedMoverTypes.map((name) => ({
                name,
                depth: 0,
                itemStyle: { color: MOVER_TYPE_COLORS[name] || '#8B6CFF' }
            })),
            ...orderedSymbols.map((name) => ({
                name,
                depth: 1
            }))
        ];

        const links: SankeyChartLink[] = dedupedLinks.map((link) => {
            const source = link.source_label || '';
            const target = link.target_label || '';
            const value = Math.max(Math.abs(Number(link.flow_value) || 0), 0.0001);

            return {
                source,
                target,
                value,
                lineStyle: { color: this.getLinkColor(source), opacity: 0.45 },
                detail: link
            };
        });

        return { nodes, links };
    }

    private hasChartData(): boolean {
        return this.getDeduplicatedDetailedLinks().length > 0;
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#sankeyMoverTypeContainer') as HTMLElement;
        if (widget) widget.classList.add('loading');

        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/equity-market/sankey-mover-type-distribution`,
                query: this.buildQueryParams(),
                headers: { }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            this.apiData = (json?.data ?? null) as SankeyApiData | null;

            if (this.hasChartData()) {
                containerWrapper?.setAttribute('data-testing-id', 'sankey-mover-type-chart');
            } else {
                containerWrapper?.removeAttribute('data-testing-id');
            }

            this.updateDisplay();
            this.emit('dataUpdate', this.apiData);
        } catch (err) {
            console.error('Error fetching sankey mover type data:', err);
            this.apiData = null;
            containerWrapper?.removeAttribute('data-testing-id');
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const containerWrapper = this.shadowRoot.querySelector('#sankeyMoverTypeContainer') as HTMLElement;
        if (!container) return;

        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }

        if (!this.hasChartData()) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        container.innerHTML = '';

        const { nodes, links } = this.buildChartPayload();
        const flowMetric = this.filters.flowMetric;

        if (!links.length) {
            container.innerHTML = getNoDataHTML();
            containerWrapper?.removeAttribute('data-testing-id');
            return;
        }

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const tooltipTheme = getEChartsTooltipTheme(widgetBox);

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                ...tooltipTheme,
                trigger: 'item',
                triggerOn: 'mousemove',
                formatter: (params: any) => {
                    if (params.dataType !== 'edge') return params.name || '';
                    const source = params.data?.source || '';
                    const target = params.data?.target || '';
                    const detail = params.data?.detail as SankeyDetailedLink | undefined;
                    const metricValue =
                        detail?.flow_value ??
                        (detail as Record<string, unknown> | undefined)?.[flowMetric] ??
                        detail?.sort_value ??
                        params.data?.value;
                    const metricLabel = this.formatMetricLabel(flowMetric);
                    const formattedValue = this.formatFlowValue(Number(metricValue));
                    const sourceDisplay = MOVER_TYPE_LABELS[source as MoverTypeKey] || source;
                    const targetDisplay = target;
                    return `
                        <div style="min-width:140px">
                            <div style="font-weight:600;margin-bottom:6px">${sourceDisplay} &gt; ${targetDisplay}</div>
                            <div>${metricLabel}: <b>${formattedValue}</b></div>
                        </div>`;
                }
            },
            series: [
                {
                    type: 'sankey',
                    left: '2%',
                    right: '12%',
                    top: '4%',
                    bottom: '4%',
                    nodeWidth: 14,
                    nodeGap: 12,
                    layoutIterations: 32,
                    emphasis: { focus: 'adjacency' },
                    label: {
                        color: textColor,
                        fontSize,
                        formatter: (params: any) => {
                            const moverType = this.getMoverTypeFromLabel(params.name);
                            if (moverType) return MOVER_TYPE_LABELS[moverType as MoverTypeKey] || params.name;
                            return params.name;
                        }
                    },
                    lineStyle: {
                        curveness: 0.5
                    },
                    data: nodes,
                    links
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
        containerWrapper?.setAttribute('data-testing-id', 'sankey-mover-type-chart');
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;
        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as
            | 'white'
            | 'black'
            | 'default-dark'
            | 'default-lite';
        this.updateDisplay();
    }

    public destroy(): void {
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach((h) => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="27" height="22" viewBox="0 0 27 22" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M22.6125 22H4.3875C3.26681 22.0001 2.18858 21.5634 1.37399 20.7794C0.559395 19.9955 0.0701982 18.9239 0.00675018 17.7843L2.29482e-08 17.5312V3.09375C-9.45295e-05 2.30983 0.291999 1.5551 0.817249 0.982089C1.3425 0.409082 2.06173 0.0605304 2.8296 0.00687518L3.0375 2.33732e-08H19.9125C20.6822 -9.62801e-05 21.4232 0.297406 21.9858 0.832383C22.5484 1.36736 22.8906 2.09991 22.9433 2.882L22.95 3.09375V4.125H23.9625C24.7322 4.1249 25.4732 4.42241 26.0358 4.95738C26.5984 5.49236 26.9406 6.22491 26.9933 7.007L27 7.21875V17.5312C27.0001 18.6727 26.5713 19.7709 25.8016 20.6006C25.032 21.4302 23.9798 21.9285 22.8609 21.9931L22.6125 22ZM4.3875 19.9375H22.6125C23.2056 19.9374 23.777 19.7102 24.2132 19.3008C24.6494 18.8914 24.9184 18.33 24.9669 17.7279L24.975 17.5312V7.21875C24.975 6.96955 24.8864 6.72878 24.7256 6.54097C24.5647 6.35316 24.3426 6.23102 24.1002 6.19712L23.9625 6.1875H22.95V16.8438C22.95 17.093 22.8614 17.3337 22.7006 17.5215C22.5397 17.7093 22.3176 17.8315 22.0752 17.8654L21.9375 17.875C21.6928 17.875 21.4564 17.7847 21.272 17.6209C21.0876 17.4571 20.9677 17.2309 20.9345 16.984L20.925 16.8438V3.09375C20.925 2.84455 20.8364 2.60378 20.6756 2.41597C20.5147 2.22816 20.2926 2.10602 20.0502 2.07212L19.9125 2.0625H3.0375C2.79283 2.06251 2.55644 2.15276 2.37204 2.31656C2.18765 2.48035 2.06773 2.70661 2.03445 2.9535L2.025 3.09375V17.5312C2.02502 18.1351 2.24798 18.7169 2.64962 19.1612C3.05125 19.6054 3.6022 19.8796 4.1931 19.9292L4.3875 19.9375ZM13.8321 14.4375H17.8902C18.1467 14.4376 18.3937 14.5368 18.5811 14.7152C18.7686 14.8936 18.8826 15.1378 18.9 15.3985C18.9175 15.6591 18.8372 15.9169 18.6753 16.1195C18.5134 16.3222 18.282 16.4548 18.0279 16.4904L17.8902 16.5H13.8321C13.5756 16.4999 13.3286 16.4007 13.1412 16.2223C12.9537 16.0439 12.8398 15.7997 12.8223 15.539C12.8048 15.2784 12.8851 15.0206 13.047 14.818C13.2089 14.6153 13.4403 14.4827 13.6944 14.4471L13.8321 14.4375ZM9.77805 9.63188C10.0466 9.63188 10.3041 9.74052 10.494 9.93392C10.6839 10.1273 10.7906 10.3896 10.7906 10.6631V15.4688C10.7906 15.7423 10.6839 16.0046 10.494 16.198C10.3041 16.3914 10.0466 16.5 9.77805 16.5H5.0598C4.79127 16.5 4.53373 16.3914 4.34385 16.198C4.15397 16.0046 4.0473 15.7423 4.0473 15.4688V10.6631C4.0473 10.3896 4.15397 10.1273 4.34385 9.93392C4.53373 9.74052 4.79127 9.63188 5.0598 9.63188H9.77805ZM8.76555 11.6944H6.0723V14.4375H8.76555V11.6944ZM13.8321 9.63188H17.8902C18.1467 9.63196 18.3937 9.73121 18.5811 9.90959C18.7686 10.088 18.8826 10.3322 18.9 10.5928C18.9175 10.8535 18.8372 11.1112 18.6753 11.3139C18.5134 11.5166 18.282 11.6491 18.0279 11.6848L17.8902 11.6944H13.8321C13.5737 11.6971 13.3242 11.599 13.1344 11.4204C12.9447 11.2417 12.8293 10.996 12.8117 10.7334C12.7941 10.4709 12.8757 10.2114 13.0398 10.0082C13.2039 9.8049 13.4381 9.67324 13.6944 9.64012L13.8321 9.63188ZM5.0598 4.81525H17.8902C18.1486 4.81257 18.3981 4.9106 18.5879 5.08925C18.7776 5.2679 18.893 5.51366 18.9106 5.7762C18.9282 6.03875 18.8466 6.29821 18.6825 6.50147C18.5184 6.70472 18.2842 6.83639 18.0279 6.8695L17.8902 6.87775H5.0598C4.80327 6.87767 4.55633 6.77841 4.36888 6.60004C4.18143 6.42166 4.06745 6.17746 4.04997 5.91679C4.03248 5.65612 4.1128 5.3984 4.27468 5.19571C4.43657 4.99303 4.66796 4.86049 4.9221 4.82487L5.0598 4.81525Z" fill="currentColor"/> </svg>`;
    }
}

declare global {
    interface Window {
        TraderverseSankeyMoverTypeDistribution: typeof TraderverseSankeyMoverTypeDistribution;
    }
}
if (typeof window !== 'undefined') {
    window.TraderverseSankeyMoverTypeDistribution = TraderverseSankeyMoverTypeDistribution;
}
